/**
 * Query string utilities with RFC 3986-friendly encoding.
 * Provides helpers to parse, stringify, and split URLs safely and predictably.
 *
 * Notes:
 * - Encoding keeps spaces as %20 (not "+").
 * - Duplicate keys become arrays when parsing.
 * - Stringification is stable (keys sorted by default).
 */

/**
 * RFC 3986-compliant encode for query components.
 * @param {string} str
 * @returns {string}
 * @example
 * encodeRFC3986("a b+c") // "a%20b%2Bc"
 */
const encodeRFC3986 = (str) =>
  encodeURIComponent(str)
    .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase())
    .replace(/%20/g, '%20');

/**
 * Ensures a path starts with a leading slash.
 * @param {string} p
 * @returns {string}
 * @example
 * ensureLeadingSlash('users') // '/users'
 */
export const ensureLeadingSlash = (p) => {
  if (!p) return '/';
  return p.startsWith('/') ? p : `/${p}`;
};

/**
 * Parses a query string into an object. Duplicate keys are collected into arrays.
 * Accepts a raw string (with or without leading '?'), URLSearchParams, or a plain object (which is returned shallow-cloned).
 * @param {string | URLSearchParams | Record<string, string | string[]> | undefined | null} qs
 * @returns {Record<string, string | string[]>}
 * @example
 * parseQuery('?a=1&a=3&b=2') // { a: ['1','3'], b: '2' }
 */
export const parseQuery = (qs) => {
  if (!qs) return {};
  let search = '';
  if (typeof qs === 'string') {
    search = qs.startsWith('?') ? qs.slice(1) : qs;
  } else if (qs instanceof URLSearchParams) {
    search = qs.toString();
  } else if (typeof qs === 'object') {
    return { ...qs };
  }
  const bag = {};
  if (!search) return bag;
  const usp = new URLSearchParams(search);
  usp.forEach((value, key) => {
    if (key in bag) {
      const cur = bag[key];
      bag[key] = Array.isArray(cur) ? [...cur, value] : [cur, value];
    } else {
      bag[key] = value;
    }
  });
  return bag;
};

/**
 * Builds a query string from a parameter object.
 * - Skips null/undefined values.
 * - Arrays become repeated keys (e.g. a=1&a=2).
 * - Keys (and array values) can be sorted for stable output.
 * @param {Record<string, string | number | boolean | (string|number|boolean)[] | undefined | null>} [params]
 * @param {{ sort?: boolean }} [options]
 * @returns {string} A leading-"?" query string or empty string when there are no entries.
 * @example
 * stringifyQuery({ a: ['3','1'], b: 2 }) // '?a=1&a=3&b=2'
 */
export const stringifyQuery = (params = {}, { sort = true } = {}) => {
  const entries = [];
  const keys = Object.keys(params);
  if (sort) keys.sort();
  for (const key of keys) {
    const val = params[key];
    if (val === undefined || val === null) continue;
    if (Array.isArray(val)) {
      const arr = sort ? [...val].map(String).sort() : val.map(String);
      for (const v of arr) entries.push(`${encodeRFC3986(key)}=${encodeRFC3986(v)}`);
    } else {
      entries.push(`${encodeRFC3986(key)}=${encodeRFC3986(String(val))}`);
    }
  }
  const qs = entries.join('&');
  return qs ? `?${qs}` : '';
};

/**
 * Splits a raw URL into path, raw search (without leading '?'), and a parsed query object.
 * @param {string} [rawUrl='/']
 * @returns {{ path: string, search: string, query: Record<string, string | string[]> }}
 * @example
 * splitQuery('/users?id=1&id=2') // { path: '/users', search: 'id=1&id=2', query: { id: ['1','2'] } }
 */
export const splitQuery = (rawUrl = '/') => {
  const s = String(rawUrl);
  const qIndex = s.indexOf('?');
  if (qIndex === -1) {
    return { path: s || '/', search: '', query: {} };
  }
  const path = s.slice(0, qIndex) || '/';
  const search = s.slice(qIndex + 1);
  return { path, search, query: parseQuery(search) };
};
